import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "data/data-access-hooks/query-keys";
import { ServiceResponse } from "andculturecode-javascript-core";
import { ProviderGoalRecord } from "models/view-models/providers/provider-goal-record";
import { ProviderSummaryRecord } from "models/view-models/providers/provider-summary-record";

// -------------------------------------------------------------------------------------------------
// #region Types
// -------------------------------------------------------------------------------------------------

export type ProviderSummarySelection = {
    years: ProviderSummaryRecord[];
    totalEvents: number;
    totalInvoiced: number;
};

// #endregion Types

// -------------------------------------------------------------------------------------------------
// #region Queries
// -------------------------------------------------------------------------------------------------

export const useGetProviderSummary = (
    queryFn: (pathParams: { id: number }) => Promise<ServiceResponse<ProviderSummaryRecord>>,
    providerId: number
) =>
    useQuery({
        queryKey: [QUERY_KEYS.PROVIDER_SUMMARY, providerId],
        queryFn: () => queryFn({ id: providerId }),
        select: getProviderSummarySelectFn,
        enabled: !!providerId,
    });

export const useGetProviderGoal = (
    queryFn: (pathParams: { id: number }) => Promise<ServiceResponse<ProviderGoalRecord>>,
    providerId: number
) =>
    useQuery({
        queryKey: [QUERY_KEYS.PROVIDER_GOAL, providerId],
        queryFn: () => queryFn({ id: providerId }),
        select: getProviderGoalSelectFn,
        enabled: !!providerId,
    });

// #endregion Queries

// -------------------------------------------------------------------------------------------------
// #region Helpers
// -------------------------------------------------------------------------------------------------

export const getProviderGoalSelectFn = (
    data: ServiceResponse<ProviderGoalRecord>
): Record<string, ProviderGoalRecord> =>
    data.resultObjects.reduce<Record<string, ProviderGoalRecord>>((acc, goal) => {
        if (goal.year !== undefined) {
            acc[goal.year.toString()] = goal;
        }
        return acc;
    }, {});

export const getProviderSummarySelectFn = (
    data: ServiceResponse<ProviderSummaryRecord>
): ProviderSummarySelection => {
    const selection = data.resultObjects.reduce<ProviderSummarySelection>(
        (acc, summary) => {
            if (summary.year !== undefined) {
                acc.years.push(summary);
            }
            acc.totalEvents += summary.totalEvents ?? 0;
            acc.totalInvoiced += summary.currentInvoiceTotal ?? 0;
            return acc;
        },
        { years: [], totalEvents: 0, totalInvoiced: 0 }
    );

    selection.years.sort((a, b) => (b.year ?? 0) - (a.year ?? 0));
    return selection;
};
// #endregion Helpers
